import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import api from "../lib/api";
import { fmtUSD, fmtNum } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { KpiCard, Loader } from "../components/KpiCard";
import {
  Wallet, TrendingUp, TrendingDown, ArrowLeftRight, PiggyBank, Award,
  AlertTriangle, Info, Bell, HeartHandshake, Building2, Users,
} from "lucide-react";

const PIE_COLORS = ["#0A2E20", "#C2410C", "#B45309", "#178070", "#8A9F95"];

const ALERT_ICON = { warning: AlertTriangle, info: Info, alert: Bell };
const ALERT_TONE = {
  warning: "text-orange-700 bg-orange-50 border-orange-200",
  info: "text-cyan-700 bg-cyan-50 border-cyan-200",
  alert: "text-amber-700 bg-amber-50 border-amber-200",
};

export default function ControlCenter() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <Loader label="Loading control center…" />;

  const netPositive = data.net_change >= 0;

  return (
    <div>
      <PageHeader
        title="Control Center"
        subtitle={`Executive financial snapshot · Fiscal Year ${data.fiscal_year}`}
      />

      {/* KPI grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-6">
        <KpiCard testid="kpi-cash" label="Cash Balance" value={fmtUSD(data.cash_balance)} icon={Wallet} />
        <KpiCard testid="kpi-revenue" label="Revenue YTD" value={fmtUSD(data.revenue_ytd)} icon={TrendingUp} accent="#2E7D32" />
        <KpiCard testid="kpi-expenses" label="Expenses YTD" value={fmtUSD(data.expenses_ytd)} icon={TrendingDown} accent="#C2410C" />
        <KpiCard testid="kpi-net" label="Net Change" value={fmtUSD(data.net_change)}
          icon={ArrowLeftRight} accent={netPositive ? "#2E7D32" : "#C2410C"} sub={netPositive ? "Surplus" : "Deficit"} />
        <KpiCard testid="kpi-funds" label="Fund Balance" value={fmtUSD(data.fund_balance)} icon={PiggyBank} accent="#178070" />
        <KpiCard testid="kpi-grants" label="Grant Balance" value={fmtUSD(data.grant_balance)} icon={Award} accent="#B45309" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Trend chart */}
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-xl p-6">
          <h2 className="text-lg font-medium text-[#1A1A1A] mb-4">Revenue vs. Expenses</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.trend} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#5C5C54" }} axisLine={{ stroke: "#E5E5E0" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#5C5C54" }} axisLine={false} tickLine={false}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E5E0", fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#0A2E20" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#C2410C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by type */}
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <h2 className="text-lg font-medium text-[#1A1A1A] mb-4">Revenue by Source</h2>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.revenue_by_type} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                {data.revenue_by_type.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E5E0", fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compliance alerts */}
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-xl p-6" data-testid="alert-center">
          <h2 className="text-lg font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Bell className="h-4 w-4 text-compliance" /> Compliance Alert Center
          </h2>
          {data.alerts.length === 0 ? (
            <p className="text-sm text-[#5C5C54]">All clear — no outstanding compliance items.</p>
          ) : (
            <div className="space-y-2.5">
              {data.alerts.map((a, i) => {
                const Icon = ALERT_ICON[a.type] || Info;
                return (
                  <button key={i} onClick={() => navigate(`/${a.module}`)}
                    data-testid={`alert-${i}`}
                    className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors duration-200 ${ALERT_TONE[a.type] || ALERT_TONE.info}`}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="text-sm flex-1">{a.message}</span>
                    <span className="text-xs opacity-70">View →</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick counts */}
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <h2 className="text-lg font-medium text-[#1A1A1A] mb-4">Organization</h2>
          <div className="space-y-3">
            {[
              { icon: HeartHandshake, label: "Donors", value: data.counts.donors, to: "/donors" },
              { icon: Building2, label: "Vendors", value: data.counts.vendors, to: "/vendors" },
              { icon: Users, label: "Programs", value: data.counts.programs, to: "/programs" },
              { icon: Award, label: "Active Grants", value: data.counts.active_grants, to: "/grants" },
            ].map((c) => (
              <button key={c.label} onClick={() => navigate(c.to)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-stoney transition-colors duration-200">
                <span className="flex items-center gap-2.5 text-sm text-[#1A1A1A]">
                  <c.icon className="h-4 w-4 text-forest" /> {c.label}
                </span>
                <span className="font-mono font-semibold text-[#1A1A1A]">{fmtNum(c.value)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
